import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  FileText,
  Lightbulb,
  Settings,
  Shield,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useAdminStats } from '@/features/admin/hooks/use-admin-stats'
import { AdminStatsCard } from '@/features/admin/components/AdminStatsCard'
import { useAuth } from '@/features/auth'

export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminDashboardPage,
})

function AdminDashboardPage() {
  useDocumentTitle('Administração')
  const { currentOrganization } = useAuth()
  const { data: stats, isLoading } = useAdminStats()

  return (
    <MainLayout title="Administração" subtitle={`Painel da clínica ${currentOrganization?.name ?? ''}`}>
      <div className="space-y-5">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-teal-50 p-2.5">
                <Shield className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-teal-700">Gestão da clínica</p>
                <h2 className="mt-1 text-xl font-semibold text-gray-900">
                  Operação, equipe e configurações
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Use esta área para acompanhar capacidade, organizar profissionais e manter a clínica pronta para os atendimentos.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to="/schedule"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <CalendarDays className="h-4 w-4 text-gray-500" />
                Agenda
              </Link>
              <Link
                to="/admin/settings"
                className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700"
              >
                <Settings className="h-4 w-4" />
                Configurações
              </Link>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <AdminStatsCard
            title="Pacientes"
            value={stats?.totalPatients ?? 0}
            subtitle={`${stats?.activePatients ?? 0} ativos`}
            icon={Users}
            isLoading={isLoading}
          />
          <AdminStatsCard
            title="Profissionais"
            value={stats?.totalProfessionals ?? 0}
            subtitle={`${stats?.activeProfessionals ?? 0} ativos`}
            icon={UserPlus}
            isLoading={isLoading}
          />
          <AdminStatsCard
            title="Relatos"
            value={stats?.totalReports ?? 0}
            subtitle={`${stats?.reportsThisMonth ?? 0} este mês`}
            icon={FileText}
            trend={
              stats?.reportsThisMonth
                ? { value: stats.reportsThisMonth, label: 'este mês', positive: true }
                : undefined
            }
            isLoading={isLoading}
          />
          <AdminStatsCard
            title="Alertas"
            value={stats?.totalInsights ?? 0}
            subtitle={`${stats?.pendingInsights ?? 0} pendentes`}
            icon={Lightbulb}
            isLoading={isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.4fr_0.9fr]">
          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900">Áreas de gestão</h3>
                <p className="text-sm text-gray-500">Atalhos para manter equipe, pacientes e indicadores em dia.</p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <ManagementCard
                to="/admin/professionals"
                icon={UserPlus}
                title="Profissionais"
                description="Adicionar, editar ou desativar acessos da equipe."
                tone="blue"
              />
              <ManagementCard
                to="/admin/patients"
                icon={Users}
                title="Pacientes"
                description="Acompanhar cadastro e vínculo dos pacientes."
                tone="teal"
              />
              <ManagementCard
                to="/admin/analytics"
                icon={BarChart3}
                title="Analytics"
                description="Ler métricas de uso, relatos e evolução da operação."
                tone="purple"
              />
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900">Resumo da clínica</h3>
                <p className="text-sm text-gray-500">Dados básicos da organização ativa.</p>
              </div>
              <Link
                to="/admin/settings"
                className="inline-flex items-center text-sm font-medium text-teal-600 hover:text-teal-700"
              >
                Editar
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            <dl className="space-y-3">
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Nome</dt>
                <dd className="mt-1 truncate text-sm font-medium text-gray-900">
                  {currentOrganization?.name ?? 'N/A'}
                </dd>
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Perfil atual</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">Administrador</dd>
              </div>
              <div className="rounded-lg bg-gray-50 px-3 py-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">ID da organização</dt>
                <dd className="mt-1 truncate font-mono text-xs text-gray-600">{currentOrganization?.id ?? 'N/A'}</dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </MainLayout>
  )
}

const cardTones = {
  blue: 'bg-blue-50 text-blue-600',
  teal: 'bg-teal-50 text-teal-600',
  purple: 'bg-purple-50 text-purple-600',
} as const

function ManagementCard({
  to,
  icon: Icon,
  title,
  description,
  tone,
}: {
  to: '/admin/professionals' | '/admin/patients' | '/admin/analytics'
  icon: LucideIcon
  title: string
  description: string
  tone: keyof typeof cardTones
}) {
  return (
    <Link
      to={to}
      className="group rounded-lg border border-gray-200 p-4 transition-colors hover:border-teal-200 hover:bg-teal-50/30"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className={`rounded-lg p-2 ${cardTones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ArrowRight className="h-4 w-4 text-gray-300 transition-colors group-hover:text-teal-600" />
      </div>
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </Link>
  )
}
